export interface UserTypeResponse {
  usertype: string;
}

export class UserTypeService {
  /**
   * Fetch user type from the backend using Clerk user ID
   * First checks Clerk metadata for faster access, then localStorage cache
   */
  // FIX: Never default to homeowner; cache role to reduce flicker
  static async getUserType(clerkUser: any): Promise<"contractor"|"homeowner"|"admin"|"unknown"> {
    const meta = clerkUser?.publicMetadata;
    const mType = meta?.usertype ?? meta?.accountType;
    if (mType === "contractor" || mType === "homeowner" || mType === "admin") return mType;

    const cached = localStorage.getItem("cb.usertype");
    if (cached === "contractor" || cached === "homeowner" || cached === "admin") return cached as any;

    // CB:BEGIN usertype-from-me
    // Use /api/me to avoid legacy 404s on /api/get-usertype
    // Only call if we have a Clerk user (signed in)
    if (!clerkUser) {
      return "unknown";
    }
    
    try {
      const { getApiUrl } = await import('./getApiUrl');
      // CB:BEGIN get-token-for-api-call
      // Get Clerk token before making API call
      const clerk = (window as any).Clerk;
      const headers: Record<string, string> = {};
      
      if (clerk?.session) {
        try {
          const token = await clerk.session.getToken();
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
        } catch (tokenError) {
          // Token retrieval failed, skip API call
          return "unknown";
        }
      } else {
        // No session, skip API call
        return "unknown";
      }
      // CB:END get-token-for-api-call
      
      const res = await fetch(getApiUrl('/api/me'), { 
        credentials: 'include',
        headers
      });
      if (res.ok) {
        const j = await res.json();
        const role = String(j?.role || '').toUpperCase();
        const mapped = role === 'ADMIN' ? 'admin' : (role === 'CONTRACTOR' ? 'contractor' : (role === 'HOMEOWNER' ? 'homeowner' : 'unknown'));
        if (mapped !== 'unknown') {
          localStorage.setItem('cb.usertype', mapped);
          return mapped as any;
        }
      }
    } catch {}
    // CB:END usertype-from-me

    return "unknown";
  }

  /**
   * Check if user should be redirected to dashboard based on user type
   */
  static shouldRedirectToDashboard(usertype: string): boolean {
    return ['homeowner', 'contractor', 'builder', 'admin'].includes(usertype);
  }

  /**
   * Get the appropriate redirect URL based on user type
   */
  static getRedirectUrl(usertype: string): string {
    switch (usertype) {
      case 'homeowner':
        return '/dashboard';
      case 'contractor':
        return '/contractor-dashboard';
      case 'builder':
        return '/builder-dashboard';
      case 'admin':
        return '/admin-dashboard';
      default:
        return '/'; // Default to landing page
    }
  }
} 